import { useCallback } from "react";
import classNames from "classnames";
import { Input as BaseInput } from "@base-ui/react/input";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import styles from "./TextArea.module.scss";
import { getTextAreaRootClassName } from "./textAreaRecipe";
import type { TextAreaControlProps, TextAreaRootProps } from "./TextArea.types";

/** Chrome-only root for recipe styling and field state. */
export const TextAreaRoot = ({
    variant = "primary",
    size,
    disabled = false,
    className,
    children,
    ref,
    ...props
}: TextAreaRootProps) => {
    const resolvedSize = size ?? "md";

    return (
        <div
            ref={ref}
            className={getTextAreaRootClassName({
                variant,
                size: resolvedSize,
                className,
            })}
            data-variant={variant}
            data-size={resolvedSize}
            data-disabled={disabled ? "" : undefined}
            {...props}
        >
            {children}
        </div>
    );
};

/**
 * Native textarea rendered through Base UI's Field-compatible control. Base UI
 * types its props against <input>, so only the props it reads for Field wiring
 * (id, name, value, disabled, autoFocus) and its own onValueChange go to it
 * directly; every textarea-typed prop rides on the render element, whose props
 * Base UI merges last.
 */
export const TextAreaControl = ({
    className,
    ref,
    id,
    name,
    value,
    defaultValue,
    disabled,
    autoFocus,
    onValueChange,
    ...props
}: TextAreaControlProps) => {
    const assignRef = useCallback(
        (node: HTMLElement | null) => {
            const textArea = node instanceof HTMLTextAreaElement ? node : null;
            mergeRefs([ref])(textArea);
        },
        [ref],
    );

    return (
        <BaseInput
            ref={assignRef}
            id={id}
            name={name}
            value={value}
            defaultValue={defaultValue}
            disabled={disabled}
            autoFocus={autoFocus}
            onValueChange={onValueChange}
            render={<textarea {...props} />}
            className={classNames(styles.control, className)}
        />
    );
};
